import React, { useEffect } from "react";
import styles from "./MisCotizacionesDeMateriales.module.scss";
import { connect } from "react-redux";
import { getMyQuotations, openMainModal, deleteQuotation } from "../../../../store/actions";
import ProfileEmptyState from "../ProfileEmptyState/ProfileEmptyState.tsx";
import { motion, AnimatePresence } from "framer-motion";
import PrivateQuotationItem from "../../../MainInfoItem/PrivateQuotationItem/PrivateQuotationItem";
import { useNavigate, useLocation } from "react-router-dom";
import { useActionGuard } from "../../../../hooks/useActionGuard.ts";
import { fireAnalytics } from "../../../../utils/firebase"
import { getSessionId, getDeviceCategory } from "../../../../utils/analyticsHelpers.ts"
import { buildNavigationParams, getUserDisplayName } from "../../../../utils/navigationHelpers.ts";
import RegisteredUserProfileLoader from "../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader";

const MisCotizacionesDeMateriales = ({ 
        getMyQuotations, 
        openMainModal,
        deleteQuotation,
        cotizaciones,
        viewQuotationsLoading,
        userData,
        profileVisualizerData,
        myPerson, 
        setInfoReady,
        profileReturnTab = 'Gestión',
        profileReturnGestionSection,
    }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const { guard } = useActionGuard();

    const targetUserId = myPerson ? userData?.id : profileVisualizerData?.id;

    const profileNavSection =
        profileReturnTab === 'Gestión'
            ? profileReturnGestionSection ?? 'cotizaciones'
            : null;

    useEffect(() => {
        if (!targetUserId) return;
        getMyQuotations(targetUserId);
    }, [getMyQuotations, targetUserId]);

    const cotizacionesToMap = cotizaciones.filter(cotizacion => cotizacion.type === "PRIVATE");

    useEffect(() => {
        if (!viewQuotationsLoading && cotizaciones !== undefined) {
            setInfoReady(true);
        }
    }, [viewQuotationsLoading, cotizaciones, setInfoReady]);

    const handleNewMaterialBudget = guard("new_material_budget", () => {
        const isFromProfile = location.pathname.startsWith('/perfil/');
        if (isFromProfile && myPerson) {
            const userId = userData?.id ? String(userData.id) : null;
            const userName = getUserDisplayName(userData);
            if (userId && userName) {
                const params = buildNavigationParams(profileReturnTab, userId, userName, profileNavSection);
                navigate(`/cotizaciondemateriales/crear${params}`);
                return;
            }
        }
        navigate("/cotizaciondemateriales/crear");
    });

    const handleOpenQuotation = (quotation) => {
        if (!quotation) return;
        const isFromProfile = location.pathname.startsWith('/perfil/');
        const targetUser = myPerson ? userData : profileVisualizerData;
        const userId = targetUser?.id ? String(targetUser.id) : null;
        const userName = getUserDisplayName(targetUser);
        const params =
            isFromProfile && userId && userName
                ? buildNavigationParams(profileReturnTab, userId, userName, profileNavSection)
                : '';

        if (myPerson) {
            navigate(`/cotizaciondemateriales/${quotation.id}${params}`);
        } else {
            navigate(`/cotizaciondemateriales/postulante/${quotation.id}${params}`);
        }
    };

    const handleEditQuotation = (quotation) => {
        if (!quotation) return;
        const isFromProfile = location.pathname.startsWith('/perfil/');
        const targetUser = myPerson ? userData : profileVisualizerData;
        const userId = targetUser?.id ? String(targetUser.id) : null;
        const userName = getUserDisplayName(targetUser);
        if (isFromProfile && myPerson && userId && userName) {
            const params = buildNavigationParams(profileReturnTab, userId, userName, profileNavSection);
            navigate(`/cotizaciondemateriales/edit/${quotation.id}${params}`);
            return;
        }
        navigate(`/cotizaciondemateriales/edit/${quotation.id}`);
    };

    const handleDeleteQuotation = (quotation) => {
        if (!quotation) return;
        openMainModal({
            title: "¿Estás seguro de querer eliminar la cotización?",
            body: "Si la eliminas no podrás recuperarla.",
            iconType: "warning",
            isSuccess: false,
            confirmHandler: () => {
                const refreshAfterDelete = () => {
                    if (targetUserId) {
                        getMyQuotations(targetUserId);
                    }
                };
                deleteQuotation(quotation.id, refreshAfterDelete);
            }
        });
    };

    return (
        <AnimatePresence mode="wait">
            <motion.div
                initial={{ opacity: 0, y: 200 }}
                animate={{ opacity: 1, y: 0 }}
                className={styles.MisCotizaciones}>
                {viewQuotationsLoading ? (
                    <div className={styles.loadingContainer}>
                        <RegisteredUserProfileLoader />
                    </div>
                ) : (
                    cotizacionesToMap && cotizacionesToMap.length > 0 ? (
                        <div className={styles.container}>
                            {cotizacionesToMap.map((cotizacion) => (
                                <PrivateQuotationItem
                                    key={cotizacion.id}
                                    quotation={cotizacion}
                                    onManage={handleOpenQuotation}
                                    onEdit={handleEditQuotation}
                                    onDelete={handleDeleteQuotation}
                                    myPerson={myPerson}
                                />
                            ))}
                        </div>
                    ) : (
                        myPerson ? (
                            <ProfileEmptyState
                                illustration="materials"
                                title="No tenés solicitudes de cotización de materiales"
                                description="Creá una solicitud para que proveedores te envíen presupuestos de materiales."
                                onRootClick={handleNewMaterialBudget}
                            />
                        ) : (
                            <ProfileEmptyState
                                illustration="materials"
                                title="Este perfil no tiene solicitudes de cotización de materiales visibles"
                                description="Las solicitudes públicas que publique van a mostrarse en esta sección."
                            />
                        )
                    )
                )}
            </motion.div>
        </AnimatePresence>
    );
}

const mapStateToProps = (state) => ({
    cotizaciones: state.profile.viewQuotations,
    viewQuotationsLoading: state.profile.viewQuotationsLoading,
    userData: state.general.userData,
});

export default connect(mapStateToProps, { getMyQuotations, openMainModal, deleteQuotation })(MisCotizacionesDeMateriales);
